import { Card, CardContent } from "@/components/ui/card";

export default function PrivacyPage() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="privacy-page">
      <p className="text-xs uppercase tracking-widest text-[#C25934] font-medium mb-2">
        Legal
      </p>
      <h1 className="font-['Outfit'] text-4xl font-semibold tracking-tight mb-2">
        Privacy Policy
      </h1>
      <p className="text-sm text-gray-500 mb-8">Last updated: February 2026</p>

      <Card className="border-[#E5E7EB]">
        <CardContent className="p-8 prose prose-neutral max-w-none">
          <h2>What we collect</h2>
          <p>
            When you use ClaimScope, we collect only what we need to help you
            win your insurance claim:
          </p>
          <ul>
            <li>
              <strong>Account info</strong> — name, email, phone, ZIP code,
              password (hashed with bcrypt, never stored in plain text).
            </li>
            <li>
              <strong>Claim data</strong> — the address, carrier, policy number,
              damage type, and description you enter.
            </li>
            <li>
              <strong>Photos you capture</strong> — including embedded EXIF
              metadata (timestamp, GPS) so carriers can verify the loss.
            </li>
            <li>
              <strong>Messages</strong> — notes you exchange with contractors
              and our adjusters within a claim.
            </li>
            <li>
              <strong>Device tokens</strong> — APNs / FCM identifiers, stored
              only so we can push you claim-status updates.
            </li>
            <li>
              <strong>Share-link telemetry</strong> — when you email a claim
              packet to an adjuster, we record the recipient email and whether
              they opened it (not who forwarded it, not the content they read).
            </li>
          </ul>

          <h2>What we do NOT collect</h2>
          <ul>
            <li>We do not sell your data to anyone. Ever.</li>
            <li>
              We do not build advertising profiles or share with ad networks.
            </li>
            <li>
              We do not track you across third-party apps or websites (no IDFA,
              no ad SDKs).
            </li>
            <li>
              We do not access contacts, calendars, microphone, or health data.
            </li>
          </ul>

          <h2>Third-party services</h2>
          <p>ClaimScope uses a small number of trusted providers:</p>
          <ul>
            <li>
              <strong>OpenAI (GPT-5.2 Vision)</strong> — your damage photos are
              sent for AI severity analysis. OpenAI&apos;s policy: photos are
              processed, not retained for training.
            </li>
            <li>
              <strong>Stripe</strong> — payment processing for the ClaimScope
              per-claim success fee and contractor memberships. Stripe handles
              card data directly; we never see it.
            </li>
            <li>
              <strong>Resend</strong> — transactional email delivery (claim
              updates, share-packet emails, receipts).
            </li>
            <li>
              <strong>MongoDB Atlas</strong> — encrypted data storage.
            </li>
          </ul>

          <h2>How we use your data</h2>
          <ul>
            <li>
              Run the core product — show you your claim, photos, messages,
              timeline, invoices.
            </li>
            <li>Email you when a claim status changes or a message arrives.</li>
            <li>Push-notify you (only if you granted permission).</li>
            <li>Generate claim packet PDFs for you to share with adjusters.</li>
            <li>Calculate anonymized analytics (avg time to approval, open
              rates by carrier domain) — never tied to an individual user.</li>
          </ul>

          <h2>Your rights</h2>
          <ul>
            <li>
              <strong>Export</strong> — we&apos;ll give you every byte we hold
              about you in JSON, within 30 days of a request.
            </li>
            <li>
              <strong>Delete</strong> — email privacy@claimscope.app and
              we&apos;ll wipe your account and all associated claims within
              30 days.
            </li>
            <li>
              <strong>Correct</strong> — you can edit most fields inside the
              app; for anything you can&apos;t, email us and we&apos;ll fix it.
            </li>
          </ul>

          <h2>Data retention</h2>
          <p>
            We keep your claim data for as long as your account is active, plus
            7 years after for tax and regulatory compliance (the IRS and most
            state insurance departments require this). You can request earlier
            deletion; we&apos;ll honor it unless we have a legal hold.
          </p>

          <h2>Children</h2>
          <p>
            ClaimScope is not intended for anyone under 18. We do not knowingly
            collect data from minors.
          </p>

          <h2>Contact</h2>
          <p>
            Questions? Email{" "}
            <a
              href="mailto:privacy@claimscope.app"
              className="text-[#C25934] hover:underline"
            >
              privacy@claimscope.app
            </a>
            .
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
